// src/components/spatial/hooks/useDrawSpaceView.js

import { useState, useCallback } from "react";
import drawSpaceConfig from "../config/drawSpaceConfig";

export default function useDrawSpaceView() {
  const {
    defaultZoom,
    minZoom,
    maxZoom,
    zoomStep,
    defaultOffsetX,
    defaultOffsetY,
  } = drawSpaceConfig.defaults;

  const [zoom, setZoom] = useState(defaultZoom);
  const [offsetX, setOffsetX] = useState(defaultOffsetX);
  const [offsetY, setOffsetY] = useState(defaultOffsetY);

  const zoomIn = useCallback(() => {
    setZoom((z) => Math.min(maxZoom, z + zoomStep));
  }, [maxZoom, zoomStep]);

  const zoomOut = useCallback(() => {
    setZoom((z) => Math.max(minZoom, z - zoomStep));
  }, [minZoom, zoomStep]);

  const resetView = useCallback(() => {
    setZoom(defaultZoom);
    setOffsetX(defaultOffsetX);
    setOffsetY(defaultOffsetY);
  }, [defaultZoom, defaultOffsetX, defaultOffsetY]);

  const pan = useCallback((dx, dy) => {
    setOffsetX((x) => x + dx);
    setOffsetY((y) => y + dy);
  }, []);

  const screenToWorld = useCallback(
    (sx, sy) => {
      const x = (sx - offsetX) / zoom;
      const y = (sy - offsetY) / zoom;
      return { x, y };
    },
    [zoom, offsetX, offsetY]
  );

  const worldToScreen = useCallback(
    (x, y) => {
      const sx = x * zoom + offsetX;
      const sy = y * zoom + offsetY;
      return { x: sx, y: sy };
    },
    [zoom, offsetX, offsetY]
  );

  const zoomAtPoint = useCallback(
    (sx, sy, deltaY) => {
      const nextZoom =
        deltaY < 0
          ? Math.min(maxZoom, zoom + zoomStep)
          : Math.max(minZoom, zoom - zoomStep);

      if (nextZoom === zoom) return;

      const worldX = (sx - offsetX) / zoom;
      const worldY = (sy - offsetY) / zoom;

      setZoom(nextZoom);
      setOffsetX(sx - worldX * nextZoom);
      setOffsetY(sy - worldY * nextZoom);
    },
    [zoom, offsetX, offsetY, minZoom, maxZoom, zoomStep]
  );

  return {
    zoom,
    offsetX,
    offsetY,

    setZoom,
    setOffsetX,
    setOffsetY,

    zoomIn,
    zoomOut,
    resetView,
    pan,
    zoomAtPoint,

    screenToWorld,
    worldToScreen,
  };
}